import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, PaperPlaneTilt, X, Ticket, ChartLineUp, CalendarBlank } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 0 }).format(n || 0);
const statusBadge = { pending: { label: "Pendiente", class: "bg-amber-100 text-amber-700" }, approved: { label: "Aprobado", class: "bg-emerald-100 text-emerald-700" }, rejected: { label: "Rechazado", class: "bg-red-100 text-red-700" } };

// Current month date constraints
const now = new Date();
const minDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
const maxDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;

export function SubmitServicePanel() {
  const { token, user } = useAuth();
  const isEngineer = user?.role === "ingeniero";
  const isSeller = user?.role === "comercial";
  const [submissions, setSubmissions] = useState({ services: [], commissions: [], totals: null });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", client: "", amount: "", percent: "5", date: new Date().toISOString().slice(0, 10), ticket_number: "" });
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/my-submissions`, { headers });
      setSubmissions(data);
    } catch (e) { if (process.env.NODE_ENV === "development") console.error(e.message); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (isEngineer) {
      if (!form.name.trim()) { toast.error("Nombre del servicio requerido"); return; }
    } else {
      if (!form.name.trim()) { toast.error("Equipo a comisionar requerido"); return; }
      if (!form.amount || parseFloat(form.amount) <= 0) { toast.error("Monto requerido"); return; }
    }
    // Validate date is current month
    if (form.date < minDate || form.date > maxDate) {
      toast.error("Solo puedes registrar servicios del mes en curso");
      return;
    }
    setSaving(true);
    try {
      const endpoint = isEngineer ? "/admin/services/submit" : "/admin/sales-commissions/submit";
      const payload = isEngineer
        ? { service_name: form.name, client_name: form.client, ticket_number: form.ticket_number, amount: 0, commission_percent: 0, date: form.date }
        : { equipment_name: form.name, client_name: form.client, amount: parseFloat(form.amount), commission_percent: parseFloat(form.percent || 5), date: form.date };
      await axios.post(`${API}${endpoint}`, payload, { headers });
      toast.success("Enviado para aprobación");
      setForm({ name: "", client: "", amount: "", percent: "5", date: new Date().toISOString().slice(0, 10), ticket_number: "" });
      setShowForm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al enviar"); }
    finally { setSaving(false); }
  };

  if (!isEngineer && !isSeller) return null;

  const items = isEngineer ? submissions.services : submissions.commissions;
  const typeLabel = isEngineer ? "Servicio" : "Venta";
  const nameField = isEngineer ? "service_name" : "equipment_name";
  const totals = submissions.totals;

  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="submit-service-panel">
      <div className={`${isEngineer ? "bg-blue-600" : "bg-teal-600"} px-5 py-3 flex items-center justify-between`}>
        <h3 className="text-white font-bold flex items-center gap-2">
          <PaperPlaneTilt size={18} weight="fill" /> Registrar {typeLabel}
        </h3>
        <button onClick={() => setShowForm(!showForm)} className="text-white/80 hover:text-white text-xs font-medium bg-white/20 px-3 py-1 rounded-full" data-testid="open-submit-form">
          <Plus size={12} className="inline mr-1" />Nuevo
        </button>
      </div>

      {/* Commission Totals */}
      {totals && (
        <div className="grid grid-cols-2 gap-0 border-b" data-testid="commission-totals">
          <div className={`px-4 py-3 ${isEngineer ? "bg-blue-50" : "bg-teal-50"}`}>
            <div className="flex items-center gap-1.5 mb-0.5">
              <CalendarBlank size={12} className="text-slate-400" />
              <span className="text-[10px] text-slate-500 uppercase font-semibold">{totals.month_name}</span>
            </div>
            <p className={`text-lg font-black ${isEngineer ? "text-blue-700" : "text-teal-700"}`} data-testid="month-commission">{fmt(totals.month_commission)}</p>
            <p className="text-[10px] text-slate-400">{totals.month_services} servicio(s) aprobado(s)</p>
          </div>
          <div className="px-4 py-3 bg-slate-50">
            <div className="flex items-center gap-1.5 mb-0.5">
              <ChartLineUp size={12} className="text-slate-400" />
              <span className="text-[10px] text-slate-500 uppercase font-semibold">Acumulado {totals.year}</span>
            </div>
            <p className="text-lg font-black text-slate-800" data-testid="year-commission">{fmt(totals.year_commission)}</p>
            <p className="text-[10px] text-slate-400">{totals.year_services} servicio(s) en el año</p>
          </div>
        </div>
      )}

      {showForm && (
        <div className="p-4 border-b bg-slate-50 space-y-3" data-testid="submit-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">{isEngineer ? "Nombre del Servicio" : "Equipo a Comisionar"} *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1 text-sm" placeholder={isEngineer ? "Ej: Mantenimiento Servidor" : "Ej: Servidor Dell R740"} data-testid="submit-name" />
            </div>
            <div>
              <Label className="text-xs">Cliente</Label>
              <Input value={form.client} onChange={e => setForm({ ...form, client: e.target.value })} className="mt-1 text-sm" placeholder="Nombre del cliente" data-testid="submit-client" />
            </div>
            {isEngineer && (
              <div>
                <Label className="text-xs flex items-center gap-1"><Ticket size={12} /> No. Ticket (Evidencia)</Label>
                <Input value={form.ticket_number} onChange={e => setForm({ ...form, ticket_number: e.target.value })} className="mt-1 text-sm" placeholder="Ej: TK-00123" data-testid="submit-ticket" />
              </div>
            )}
            <div>
              <Label className="text-xs">Fecha (solo mes en curso)</Label>
              <Input type="date" value={form.date} min={minDate} max={maxDate} onChange={e => setForm({ ...form, date: e.target.value })} className="mt-1 text-sm" data-testid="submit-date" />
            </div>
            {isSeller && (
              <>
                <div>
                  <Label className="text-xs">Monto Total ($) *</Label>
                  <Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="mt-1 text-sm" placeholder="0" data-testid="submit-amount" />
                </div>
                <div>
                  <Label className="text-xs">% Comisión</Label>
                  <Input type="number" value={form.percent} onChange={e => setForm({ ...form, percent: e.target.value })} className="mt-1 text-sm" data-testid="submit-percent" />
                </div>
              </>
            )}
            {isSeller && form.amount && form.percent && (
              <div className="flex items-end">
                <p className="text-sm font-bold text-teal-700">
                  Comisión: {fmt(parseFloat(form.amount || 0) * parseFloat(form.percent || 0) / 100)}
                </p>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSubmit} disabled={saving} className={isEngineer ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-teal-600 hover:bg-teal-700 text-white"} data-testid="submit-send">
              <PaperPlaneTilt size={14} className="mr-1" /> {saving ? "Enviando..." : "Enviar para Aprobación"}
            </Button>
            <Button variant="outline" onClick={() => setShowForm(false)}><X size={14} className="mr-1" /> Cancelar</Button>
          </div>
        </div>
      )}

      {/* My Submissions */}
      <div className="p-4">
        {items.length === 0 ? (
          <p className="text-center text-sm text-slate-400 py-4">Sin registros. Usa el botón "Nuevo" para enviar tu primer {typeLabel.toLowerCase()}.</p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="submissions-list">
            {items.map(item => {
              const st = statusBadge[item.approval_status] || statusBadge.pending;
              return (
                <div key={item.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50" data-testid={`submission-${item.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-800 truncate">{item[nameField]}</span>
                      <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${st.class}`}>{st.label}</span>
                    </div>
                    <div className="flex items-center gap-3 text-[10px] text-slate-500 mt-0.5">
                      <span className="font-mono">{item.date}</span>
                      <span>{item.client_name}</span>
                      {item.ticket_number && <span className="flex items-center gap-0.5 text-blue-600 font-medium"><Ticket size={10} /> {item.ticket_number}</span>}
                    </div>
                    {item.approval_status === "rejected" && item.rejection_reason && (
                      <p className="text-[10px] text-red-500 mt-1">Motivo: {item.rejection_reason}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    {item.amount > 0 ? (
                      <>
                        <p className="text-sm font-bold text-slate-800">{fmt(item.amount)}</p>
                        <p className={`text-xs font-semibold ${isEngineer ? "text-blue-600" : "text-teal-600"}`}>{fmt(item.commission_amount)}</p>
                      </>
                    ) : (
                      <span className="text-[10px] text-slate-400 italic">Monto pendiente</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
